import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { PixieAnswer, PixieEntry } from '@/features/pixie/types';
import { useFormatter } from '@/shared/i18n/useFormatter';

/* Conversation state lives here rather than in the panel, so closing Pixie does
   not throw away what it told you. The panel — and everything it fetches — is
   only mounted while it is open. */

interface PixieContextValue {
  readonly open: boolean;
  readonly log: readonly PixieEntry[];
  /** The question waiting for an answer, or null when Pixie is idle. */
  readonly pending: string | null;
  readonly ask: (question: string) => void;
  readonly say: (answer: PixieAnswer) => void;
  readonly resolve: (answer: PixieAnswer) => void;
  readonly toggle: () => void;
  readonly close: () => void;
}

const PixieContext = createContext<PixieContextValue | null>(null);

export function PixieProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState<readonly PixieEntry[]>([]);
  const [pending, setPending] = useState<string | null>(null);
  const nextId = useRef(0);

  /* The turn marker. Through the formatter rather than `toLocaleTimeString`,
     which takes the machine's locale and would stamp a German conversation in
     whatever format the operating system was installed in. */
  const format = useFormatter();

  const push = useCallback(
    (from: PixieEntry['from'], answer: PixieAnswer) => {
      nextId.current += 1;
      const entry: PixieEntry = { id: String(nextId.current), from, at: format.clock(), answer };
      setLog((entries) => [...entries, entry]);
    },
    [format],
  );

  const value = useMemo<PixieContextValue>(
    () => ({
      open,
      log,
      pending,
      ask: (question) => {
        const text = question.trim();
        if (!text) return;
        setOpen(true);
        push('you', { text });
        setPending(text);
      },
      say: (answer) => push('pixie', answer),
      resolve: (answer) => {
        push('pixie', answer);
        setPending(null);
      },
      toggle: () => setOpen((wasOpen) => !wasOpen),
      close: () => setOpen(false),
    }),
    [open, log, pending, push],
  );

  return <PixieContext.Provider value={value}>{children}</PixieContext.Provider>;
}

export function usePixie(): PixieContextValue {
  const value = useContext(PixieContext);
  if (!value) throw new Error('usePixie must be used inside a PixieProvider');
  return value;
}
